import {Radio} from "tea-component";
import React, {useEffect, useMemo, useRef} from "react";
import classnames from "classnames";
import {TIncludeOrExclude, IStringSelectorButtonProps, IStringSelectorWithTagProps,} from "@/pages/datalab/components/StringSelectorWithTag/type";

import {getNullTextOfFilter, useDblIntl} from "@/pages/datalab/utils/utils";
import { FilterStringOptions } from '@dbl/core/filter';
import FilterSelectorWithTag from "@/pages/datalab/components/FilterSelectorWithTag";
import OverallAllSection from '@/pages/datalab/components/StringSelectorWithTag/OverallAllSection';
import './index.scss';
import {isEmpty} from 'lodash-es';
import {useTransformNullValue} from '@/pages/datalab/components/StringSelectorWithTag/hooks';
import { useErrorCard } from '@dbl/domainComponents/stateDisplay';
import { isOverallAllState } from '@/pages/datalab/components/StringSelectorWithTag/stringSelectorStateUtils';

export const StringSelectorButton = (props: IStringSelectorButtonProps) => {
  const {
    name = '',
    isAllState,
    stringValues,
    operation,
    commonFormatMessage,
    isExclude,
    options,
  } = props;

  // 按 options 将 value 转为当前语言的展示文案，无 options 则用 value
  const getDisplayText = (value: string): string => {
    if (options?.length) {
      const opt = options.find((o) => String(o.value ?? '') === String(value));
      return (opt?.text ?? opt?.title ?? getNullTextOfFilter(value)) as string;
    }
    return getNullTextOfFilter(value) as string;
  };

  let content: string | number | boolean;
  let contentArray: string[] = []; // 保存处理后的文本数组，用于 isExclude 时正确分割

  if (isAllState) {
    content = commonFormatMessage('all');
  } else if (stringValues?.length > 2) {
    content = stringValues?.length > 1000 ? '+1000' : stringValues?.length;
  } else {
    contentArray =
      stringValues?.length <= 2
        ? stringValues?.map((item) => getDisplayText(item))
        : [];
    content = contentArray.join(',') || '';
  }

  // 排除时，需要将内容用 del 包裹
  const renderContent = useMemo(() => {
    if (!isExclude) {
      return content;
    }
    // 如果有保存的数组，直接使用数组，避免文本中的逗号被错误分割
    if (contentArray && contentArray?.length > 0) {
      return contentArray?.map((item, index) => (
        <React.Fragment key={index}>
          <del>{item}</del>
          {index < contentArray?.length - 1 && ','}
        </React.Fragment>
      ));
    }
    // 其他情况（数字、'all' 等）整个用 del 包裹
    return <del>{content}</del>;
  }, [isExclude, content, contentArray]);

  return (
    <div className={`selector-multiple__header select-button`} style={{display: 'flex'}}>
      <span className="dbl-select-button-text">
        {name}
      </span>
      { operation ? <span className={'dbl-select-button-operation'}>
        {/* ({commonFormatMessage(operation)}) */}
      </span> : ''}
      {
        content  ? (
          <span className="dbl-select-button-value">
            {renderContent}
          </span>
        ) :
          (<span className={'dbl-select-button-placeholder dbl-select-button-value'}>{commonFormatMessage('string_selector_placeholder')}</span>)
      }
      {/* {*/}
      {/*  loading ? (<Icon type="loading" />) : ''*/}
      {/* }*/}
    </div>
  );
};


const StringSelectorWithTag = (props: IStringSelectorWithTagProps) => {
  const {
    name = '',// 筛选器的名称
    extName = '', // 扩展名称
    options, // 显示的选项
    allOptions, // 原始选项列表
    isExclude, // 是否排除
    onOperationChange, // 包含, 排除切换
    operation='include', // 包含, 排除的值
    onCheckChange, // 勾选或取消复选项, 第二个参数是代表是否全选
    values=[], // 勾选的值
    isAllState = true, // 是否是全选状态
    isClearSearchWord, // 是否清空搜索框
    
    total,// 全部选项的真实数量, 前端只会展示1000条
    showOperationRadio = true, // 是否展示包含/排除
    maxTagCount = 100, // tag显示最大数量
    listHeight = 300,

    // 搜索相关属性
    onSearch,
    onSearchKeywordChange,
    onClearTags,
    searchState,

    // 选项加载状态
    loading = false,
    error ,
  
    // 插入的额外组件
    topContentRender,
    
    
    // 下拉弹窗
    buttonRender,
    className,
    boxClassName,
    
    ...dropdownProps
  
  } = props;
  const {commonFormatMessage, isEn} = useDblIntl('');
  const filterTagRef = useRef<{
    searchWord?: string;
    clearSearchWord?: () => void;
  } | null>(null);
  
  const searchInputRef = useRef<HTMLInputElement>(null);

  const isOverallAll = isOverallAllState(isAllState, values);
  const prevIsOverallAllRef = useRef(isOverallAll);
  
  useEffect(() => {
    !loading && searchInputRef.current?.focus?.();
    // 初始化结束后可以 滚动到搜索框位置展示
    // 搜索时, 已经展示出是搜索框, 搜索后再获取焦点也可以
  }, [loading]);

  useEffect(() => {
    // 仅切换到 Overall All 态时清空搜索，避免 Overall All 下输入首字符后被误清
    if (!loading && isOverallAll && !prevIsOverallAllRef.current) {
      filterTagRef?.current?.clearSearchWord?.();
    }
    prevIsOverallAllRef.current = isOverallAll;

    if (isClearSearchWord && filterTagRef?.current?.searchWord) {
      filterTagRef?.current?.clearSearchWord?.();
    }
  }, [isOverallAll, loading, isClearSearchWord]);
  const {finallyValues, onChange} = useTransformNullValue(props);
  
  const faildMsg = useErrorCard(error);

  return <FilterSelectorWithTag
    ref={filterTagRef}
    name={name}
    autoClearSearchValue={false}
    searchPlaceholder={commonFormatMessage('search')}
  
    // 选项相关
    isFull={isAllState}
    values={finallyValues} // 勾选的值
    options={options} // 显示的选项
    allOptions={allOptions} // 原始选项列表
    virtual={true} // 是否启动虚拟列表
    listHeight={listHeight} // 列表高度
    maxShowTagCount={maxTagCount}
    maxRemainingCount={1000}

    // 选项加载状态
    loading={loading}
    error={error}
    failedMsg={faildMsg}
    nodataMsg={commonFormatMessage('no_data')}

    onSearchKeywordChange={onSearchKeywordChange}
    onSearch={onSearch}
    searchState={searchState}
    onClearTags={onClearTags}

    // 插入的额外组件
    topContentRender = {(close) => (<>
      {/* 外界插入的组件 */}
      {topContentRender?.(close)}
  
      {/* 包含 排除 */}
      {showOperationRadio && (
        <Radio.Group
          className={'dbl-string-selector-include-radio'}
          value={operation}
          onChange={(value) => {
            onOperationChange?.(value as TIncludeOrExclude);
          }}
        >
          {FilterStringOptions.map((listItem) => (
            <Radio key={listItem.value} name={listItem.value}>
              {isEn ? listItem.text_en : listItem.text}
            </Radio>
          ))}
        </Radio.Group>
      )}
    </>)}

    beforeOptionsRender={({
      isFull,
      values: contextValues,
      internalSearchState,
      handleValuesChange,
    }) => (
      <OverallAllSection
        isSelected={!!isFull && isEmpty(contextValues)}
        isHidden={internalSearchState}
        onSelect={() => {
          handleValuesChange({ values: [], isFull: true });
          filterTagRef?.current?.clearSearchWord?.();
        }}
      />
    )}

    // 下拉弹窗
    button={buttonRender ? buttonRender?.(<StringSelectorButton
      name={(name || "") + (extName || "")}
      operation={operation}
      isExclude={isExclude}
      isAllState={isAllState}
      stringValues={values}
      commonFormatMessage={commonFormatMessage}
      total={total}
      options={allOptions || options}
    />) : (<StringSelectorButton
      name={(name || "") + (extName || "")}
      isAllState={isAllState}
      isExclude={isExclude}
      stringValues={values}
      commonFormatMessage={commonFormatMessage}
      total={total}
      options={allOptions || options}
    />)} // 按钮文本展示，options 用于按当前语言展示选中项文案
    
    {...dropdownProps}
    className={classnames('dbl-string-selector-dropdown', className)}
    boxClassName={classnames('dbl-string-selector-dropdown-box', boxClassName)}
    onChange={onChange}
    boxAdaptable
    flipped={false}
  />;
  
};

export default StringSelectorWithTag;